import * as React from 'react';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import RawTextField from '../components/RawTextField.js';
import Menu, { MenuInteractionType, MenuAlignment, MenuSize } from '@doordash/component-menu';
import { IntlPhoneFieldSize, FieldSizeSemantic, FieldSizeString } from '../constants.js';
import CountryCodeHelper from './CountryCodeHelper.js';
import CountryCodeHelperControl from './CountryCodeHelperControl.js';
import { generateUniqueId, COUNTRY_CODES_TO_DIAL_CODE, AsYouType } from '@doordash/utilities';
import { StackChildren, StackChildrenSize, InlineChildren, InlineChildrenAlignment, InlineChildrenJustification, InlineChildrenSize, Text, TextStyle, TextColor, Spacing } from '@doordash/design-language';
import { getInternationalNumber } from './utils/getInternationalNumber.js';
import { getInvalidPhoneNumber } from './utils/getInvalidPhoneNumber.js';
import { getIsIE } from './utils/getIsIE.js';
import { getMenuControlText } from './utils/getMenuControlText.js';
import { isNumeric } from './utils/isNumeric.js';
import { parsePhoneNumber } from './utils/parsePhoneNumber.js';
import ErrorMessage from '../components/ErrorMessage.js';
import useSize from '@react-hook/size';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var fragmentRenderer = function fragmentRenderer() {
  return /*#__PURE__*/React.createElement(React.Fragment, null);
};
/**
 * International first component optimized for country code searching and phone number validation.
 *
 * If a given application should wants to limit the country dialing code of a phone number,
 * that validation should be done at the application level, and not at the phone number
 * input component level.
 *
 * The input is comprised of a `CountryCodeHelper`, which includes a localized list of
 * countries displaying their name and dial code with a fuzzy-search enabled
 * search bar, and the actual telephone number input.
 *
 * This component **must** be used as a child of a `LayerManager`, since the
 * `CountryCodeHelper` requires it. This global requirement should be removed
 * in the future by using alternative approaches such as Portal.
 */


var IntlPhoneField = function IntlPhoneField(_ref) {
  var _ref$preferredCountry = _ref.preferredCountryCodes,
      preferredCountryCodes = _ref$preferredCountry === void 0 ? ['US', 'CA', 'AU', 'JP', 'NZ'] : _ref$preferredCountry,
      _ref$maxCountriesDisp = _ref.maxCountriesDisplayed,
      maxCountriesDisplayed = _ref$maxCountriesDisp === void 0 ? 5 : _ref$maxCountriesDisp,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? 'en-US' : _ref$locale,
      value = _ref.value,
      initCurrentCountry = _ref.initCurrentCountry,
      errors = _ref.errors,
      label = _ref.label,
      description = _ref.description,
      hint = _ref.hint,
      isInitFocused = _ref.isInitFocused,
      isLabelHidden = _ref.isLabelHidden,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? IntlPhoneFieldSize.Medium : _ref$size,
      isDisabled = _ref.isDisabled,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      onCountryCodeHelperToggle = _ref.onCountryCodeHelperToggle,
      id = _ref.id,
      props = _objectWithoutProperties(_ref, ["preferredCountryCodes", "maxCountriesDisplayed", "locale", "value", "initCurrentCountry", "errors", "label", "description", "hint", "isInitFocused", "isLabelHidden", "size", "isDisabled", "onChange", "onFocus", "onBlur", "onCountryCodeHelperToggle", "id"]);

  /**
   * Always ensure we are working with a parsable, valid international number.
   */
  var intlPhoneNumberValue = getInternationalNumber(value);
  var fieldId = useMemo(function () {
    return id || generateUniqueId();
  }, [id]);

  var _useState = useState(function () {
    return parsePhoneNumber(intlPhoneNumberValue).countryCode || initCurrentCountry;
  }),
      _useState2 = _slicedToArray(_useState, 2),
      currentCountry = _useState2[0],
      setCurrentCountry = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isMenuVisible = _useState4[0],
      setIsMenuVisible = _useState4[1];

  var _useState5 = useState(),
      _useState6 = _slicedToArray(_useState5, 2),
      generatedErrors = _useState6[0],
      setGeneratedErrors = _useState6[1];

  var _useState7 = useState(),
      _useState8 = _slicedToArray(_useState7, 2),
      generatedErrorIds = _useState8[0],
      setGeneratedErrorIds = _useState8[1];

  var phoneNumberInfo = useMemo(function () {
    return parsePhoneNumber(intlPhoneNumberValue, currentCountry);
  }, [currentCountry, intlPhoneNumberValue]);
  var isIE = getIsIE();
  /**
   * Emit onChange callback based on changes
   */

  useEffect(function () {
    var number = phoneNumberInfo.number;

    if (intlPhoneNumberValue !== number && typeof onChange === 'function') {
      onChange(phoneNumberInfo, undefined);
    }
  }, [onChange, phoneNumberInfo, intlPhoneNumberValue]);
  /**
   * Auto update the current country based on the parsed phone number input
   */

  useEffect(function () {
    var countryCode = phoneNumberInfo.countryCode;

    if (countryCode && countryCode !== currentCountry) {
      setCurrentCountry(countryCode);
    }
  }, [currentCountry, phoneNumberInfo]);

  var handleTextFieldChange = function handleTextFieldChange(next, e) {
    if (next.length < 2) {
      setCurrentCountry(undefined);
    } // Filter for proper E.164 format


    var filtered = next.split('').filter(function (entry, i) {
      if (i === 0) {
        return entry === '+' || isNumeric(entry);
      }

      return isNumeric(entry);
    }).join('');
    var nextPhoneNumberInfo = parsePhoneNumber(filtered, currentCountry);
    onChange(nextPhoneNumberInfo, e);
  };

  var handleMenuToggle = useCallback(function () {
    setIsMenuVisible(function (prev) {
      if (typeof onCountryCodeHelperToggle === 'function') {
        onCountryCodeHelperToggle(!prev);
      }

      return !prev;
    });
  }, [onCountryCodeHelperToggle]);
  var handleCountrySelect = useCallback(function (selectedCountryCode) {
    setIsMenuVisible(false);

    if (selectedCountryCode === currentCountry) {
      return;
    }

    setCurrentCountry(selectedCountryCode);
    var nextPhoneNumber = getInvalidPhoneNumber( // casting here as a temp bridge between the mismatches in the library CountryCode type and the country codes in our constant
    "+".concat(COUNTRY_CODES_TO_DIAL_CODE[selectedCountryCode]), selectedCountryCode);
    onChange(nextPhoneNumber, undefined);
  }, [currentCountry, onChange]);
  var containerRef = useRef(null);

  var _useSize = useSize(containerRef, {
    initialWidth: 0,
    initialHeight: 0
  }),
      _useSize2 = _slicedToArray(_useSize, 1),
      containerWidthPx = _useSize2[0];

  var memoizedMenuContent = useMemo(function () {
    return [{
      customContent: /*#__PURE__*/React.createElement(CountryCodeHelper, {
        locale: locale,
        preferredCountryCodes: preferredCountryCodes,
        maxCountriesDisplayed: maxCountriesDisplayed,
        onCountrySelect: handleCountrySelect,
        size: size,
        minWidthPx: containerWidthPx
      })
    }];
  }, [containerWidthPx, handleCountrySelect, locale, maxCountriesDisplayed, preferredCountryCodes, size]);
  var formatter = useMemo(function () {
    return new AsYouType(currentCountry);
  }, [currentCountry]);
  var formattedValue = useMemo(function () {
    formatter.reset();
    /**
     * Note: The cursor jumps to the end of the input when characters in
     * the middle before the first formatted space is deleted, because the
     * input is being recreated and arbitrary spaces are being added.
     */

    return formatter.input(intlPhoneNumberValue);
  }, [formatter, intlPhoneNumberValue]);
  var doesNumberInputHaveError = errors && errors.length > 0 && errors.some(function (e) {
    return e.type === 'number';
  });
  var doesCodeHelperHaveError = errors && errors.length > 0 && errors.some(function (e) {
    return e.type === 'countrySelector';
  }); // Intl.DisplayNames API is not supported in IE.
  // See: https://caniuse.com/mdn-javascript_builtins_intl_displaynames_displaynames

  var isCountryCodeHelperSupported = !isIE && !isDisabled; // map and generate error IDs here, store in state to connect error messages to
  // input field via `accessibilityDescribedBy` prop

  useEffect(function () {
    if (errors && errors.length) {
      var errorDetails = [];
      var errorIds = [];
      errors.forEach(function (errorObj) {
        var errorId = errorObj.id ? errorObj.id : generateUniqueId();
        errorDetails.push({
          message: errorObj.message,
          genId: errorId
        });
        errorIds.push(errorId);
      });
      setGeneratedErrors(errorDetails);
      setGeneratedErrorIds(errorIds.join(' '));
    } else {
      setGeneratedErrors(undefined);
      setGeneratedErrorIds(undefined);
    }
  }, [errors]);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef
  }, /*#__PURE__*/React.createElement(StackChildren, {
    size: StackChildrenSize.XxSmall
  }, label && !isLabelHidden && /*#__PURE__*/React.createElement(InlineChildren, {
    alignItems: InlineChildrenAlignment.FlexEnd,
    justifyContent: InlineChildrenJustification.SpaceBetween,
    size: InlineChildrenSize.Medium
  }, /*#__PURE__*/React.createElement(Text, {
    styles: TextStyle["".concat(FieldSizeSemantic[size], "Label")],
    color: isDisabled ? TextColor.TextDisabled : TextColor.TextPrimary,
    tag: "label",
    htmlFor: fieldId
  }, label), description ? /*#__PURE__*/React.createElement(Text, {
    id: "".concat(fieldId, "-description"),
    styles: TextStyle["".concat(FieldSizeSemantic[size], "Description")],
    color: isDisabled ? TextColor.TextDisabled : TextColor.TextSecondary
  }, description) : null), /*#__PURE__*/React.createElement(InlineChildren, {
    size: InlineChildrenSize.None
  }, isCountryCodeHelperSupported ? /*#__PURE__*/React.createElement(Menu, {
    interactionType: MenuInteractionType.Controlled,
    alignMenu: MenuAlignment.BottomLeft,
    isInline: true,
    content: memoizedMenuContent,
    isVisible: isMenuVisible,
    onVisibilityChange: handleMenuToggle,
    size: MenuSize[FieldSizeString[size]],
    renderMenuControl: function renderMenuControl(_ref2) {
      var accessibilityAttributes = _ref2.accessibilityAttributes,
          describedBy = _ref2.describedBy;
      return /*#__PURE__*/React.createElement(CountryCodeHelperControl, {
        accessibilityAttributes: _objectSpread({
          'aria-describedby': describedBy
        }, accessibilityAttributes),
        isExpanded: isMenuVisible,
        hasError: doesCodeHelperHaveError,
        clickHandler: handleMenuToggle,
        sizeString: FieldSizeString[size],
        controlText: getMenuControlText(currentCountry)
      });
    },
    menuContainerStyles: {
      padding: "".concat(Spacing.Small, "px 0")
    }
  }) : null, /*#__PURE__*/React.createElement(RawTextField, _extends({}, props, {
    size: size,
    type: "tel",
    value: formattedValue,
    autoFocus: isInitFocused,
    isDisabled: isDisabled,
    onFocus: onFocus,
    onBlur: onBlur,
    description: description,
    isLabelHidden: true,
    label: label,
    onChange: handleTextFieldChange,
    id: fieldId,
    error: doesNumberInputHaveError,
    accessibilityDescribedBy: generatedErrorIds
    /**
     * Note: Workaround to remove border-radius on the left side of the input.
     */
    ,
    renderConnectedBefore: isCountryCodeHelperSupported ? fragmentRenderer : undefined
  }))), generatedErrors && generatedErrors.length ? generatedErrors.map(function (error) {
    return /*#__PURE__*/React.createElement(ErrorMessage, {
      key: error.genId,
      error: error.message,
      size: size,
      id: error.genId
    });
  }) : null, hint ? /*#__PURE__*/React.createElement(Text, {
    styles: TextStyle["".concat(FieldSizeSemantic[size], "Hint")],
    color: TextColor.TextTertiary
  }, hint) : null));
};

IntlPhoneField.Sizes = IntlPhoneFieldSize;

export { IntlPhoneField as default };
